import { FreeRect, splitFreeRects } from './nestOnSheet';

// Axis-aligned rectangle on the sheet, mm, relative to the sheet's top-left corner.
export type SheetRect = { xMm: number; yMm: number; widthMm: number; heightMm: number };

const EPS = 1e-9;

export interface FreeSpotFinder {
  // The top-most, then left-most position where a widthMm × heightMm piece fits, or
  // undefined when it does not fit anywhere.
  find: (piece: { widthMm: number; heightMm: number }) => { xMm: number; yMm: number } | undefined;
  // Marks a rect as taken, so later finds keep gapMm clear of it.
  claim: (rect: SheetRect) => void;
}

// Free space on a sheet already holding `obstacles`, kept at least gapMm away from each of
// them. The obstacles never move: this is how new artwork joins a rolling gang sheet
// without disturbing the layout the buyer already has. Uses the same MaxRects free-rectangle
// split as nestOnSheet, with the same right/bottom gap padding on both the piece and the
// obstacles. The free rects are split once per obstacle and once per claim, so placing many
// copies one after another does not redo the whole split for each of them.
export function createFreeSpotFinder(
  sheet: { widthMm: number; heightMm: number },
  obstacles: SheetRect[],
  gapMm: number,
): FreeSpotFinder {
  const innerW = sheet.widthMm + gapMm;
  const innerH = sheet.heightMm + gapMm;
  let freeRects: FreeRect[] = innerW <= EPS || innerH <= EPS ? [] : [{ x: 0, y: 0, w: innerW, h: innerH }];

  const claim = (rect: SheetRect) => {
    freeRects = splitFreeRects(freeRects, {
      x: rect.xMm,
      y: rect.yMm,
      w: rect.widthMm + gapMm,
      h: rect.heightMm + gapMm,
    });
  };
  obstacles.forEach(claim);

  const find = (piece: { widthMm: number; heightMm: number }) => {
    const pieceW = piece.widthMm + gapMm;
    const pieceH = piece.heightMm + gapMm;
    let best: FreeRect | undefined;
    freeRects.forEach((rect) => {
      if (rect.w - pieceW < -EPS || rect.h - pieceH < -EPS) {
        return;
      }
      if (!best || rect.y < best.y - EPS || (Math.abs(rect.y - best.y) <= EPS && rect.x < best.x - EPS)) {
        best = rect;
      }
    });
    return best ? { xMm: best.x, yMm: best.y } : undefined;
  };

  return { find, claim };
}

// One-off form of createFreeSpotFinder: where a single piece fits among `obstacles`.
function findFreeSpot(
  sheet: { widthMm: number; heightMm: number },
  obstacles: SheetRect[],
  piece: { widthMm: number; heightMm: number },
  gapMm: number,
): { xMm: number; yMm: number } | undefined {
  return createFreeSpotFinder(sheet, obstacles, gapMm).find(piece);
}

export default findFreeSpot;
